import { DISCLOSURE_BUTTON_CLASS } from './Constants';
import {
    findAssociatedLink,
    findButtonForSubmenu,
    getJQuery,
    getSmartMenusInstance,
    clearShowcaseDismissClass,
    isShowcaseWrapper,
} from './Utils';

let $: JQueryStatic;

// =============================================================================
// SmartMenus Event Interception — Disable Link Menu Toggling
// =============================================================================

/**
 * Prevent SmartMenus from opening submenus when a link with a disclosure
 * button is clicked or activated via keyboard (Enter/Space).
 *
 * SmartMenus binds handlers with $.proxy() at init time, which captures
 * method references — prototype overrides applied after init have no effect.
 * Instead, we register native capture-phase listeners that fire before
 * jQuery's bubble-phase delegation, calling stopPropagation() so SmartMenus
 * never sees the event.
 *
 * - Click (including Enter-triggered clicks): stopped so itemClick won't fire.
 *   The browser's default navigation still occurs (no preventDefault).
 * - Space keydown: stopped so rootKeyDown won't call itemClick.
 */
const overrideSmartMenusLinkBehavior = (menuSelectors: readonly string[]): void => {
    menuSelectors.forEach((selector) => {
        const menuEl = document.querySelector<HTMLElement>(selector);
        if (!menuEl) {
            return;
        }

        menuEl.addEventListener(
            'click',
            (e: Event) => {
                const target = e.target as HTMLElement;
                const $a = $(target).closest('a');
                if (!$a.length || !$a.siblings(`.${DISCLOSURE_BUTTON_CLASS}`).length) {
                    return;
                }
                e.stopPropagation();
            },
            true,
        );

        menuEl.addEventListener(
            'keydown',
            (e: KeyboardEvent) => {
                if (e.key !== ' ') {
                    return;
                }
                const $target = $(e.target as HTMLElement);
                if (!$target.is('a') || !$target.siblings(`.${DISCLOSURE_BUTTON_CLASS}`).length) {
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
            },
            true,
        );
    });
};

// =============================================================================
// Menu Toggle via SmartMenus API
// =============================================================================

/**
 * Toggle a submenu's visibility via the SmartMenus API.
 * Coordinates with SmartMenus rather than manipulating the DOM directly.
 */
const toggleMenuViaButton = ($button: JQuery): void => {
    const $link = findAssociatedLink($button);
    if (!$link || !$link.length) {
        return;
    }

    const $sub = $link.dataSM('sub') as JQuery | undefined;
    if (!$sub || !$sub.length) {
        return;
    }

    const smartmenus = getSmartMenusInstance($button);
    if (!smartmenus) {
        return;
    }

    if ($sub.is(':visible')) {
        smartmenus.menuHide($sub);
        $button.attr('aria-expanded', 'false');
        $button.focus();
    } else {
        // Remove any Esc-dismissal class so the keyboard re-open path is not
        // blocked (see SHOWCASE_DISMISSED_CLASS in Utils.ts).
        clearShowcaseDismissClass($sub);
        smartmenus.itemActivate($link, true);
        $button.attr('aria-expanded', 'true');
    }
};

// =============================================================================
// Keyboard Handlers
// =============================================================================

/**
 * Handle keydown events on disclosure buttons.
 *
 * - Enter/Space: Toggle the associated submenu.
 * - Escape is NOT handled here; SmartMenus handles it globally.
 *   Button state is synced via SmartMenus events in setupSmartMenusEventListeners.
 */
const handleButtonKeydown = (e: JQuery.KeyDownEvent): void => {
    const key = e.key;

    if (key === 'Enter' || key === ' ') {
        e.preventDefault();
        toggleMenuViaButton($(e.currentTarget));
    }
};

/**
 * Bind keyboard and click event handlers for all disclosure buttons.
 */
const setupKeyboardHandlers = (menuSelectors: readonly string[]): void => {
    menuSelectors.forEach((selector) => {
        $(selector).on('keydown', `.${DISCLOSURE_BUTTON_CLASS}`, handleButtonKeydown);
        $(selector).on('click', `.${DISCLOSURE_BUTTON_CLASS}`, (e: JQuery.ClickEvent) => {
            e.preventDefault();
            toggleMenuViaButton($(e.currentTarget));
        });
    });
};

// =============================================================================
// SmartMenus Event Listeners — State Synchronization
// =============================================================================

/**
 * Listen to SmartMenus events to keep disclosure button state in sync.
 *
 * When SmartMenus shows or hides a submenu (via hover, Escape, outside click,
 * or any other mechanism), we update the corresponding disclosure button's
 * aria-expanded attribute.
 *
 * For showcase wrappers specifically, SmartMenus manages aria-hidden on the
 * outer wrapper ($sub) in its menuShow/menuHide cycle, but it does not touch
 * the inner .ss-submenu-links > ul (the real nav list). On hover that list is
 * updated by activateShowcaseMenu (SmartMenus skips menuShow for CSS-shown
 * menus). On keyboard SmartMenus runs menuShow directly and fires these SMAPI
 * events, so we update the inner list here to cover that path.
 */
const setupSmartMenusEventListeners = (menuSelectors: readonly string[]): void => {
    menuSelectors.forEach((selector) => {
        const $menu = $(selector);

        $menu.on('beforeshow.smapi', (_e, menu) => {
            const $sub = $(menu);
            const $button = findButtonForSubmenu($sub);
            if ($button.length) {
                $button.attr('aria-expanded', 'true');
            }
            if (isShowcaseWrapper($sub)) {
                $sub.find('.ss-submenu-links > ul').attr('aria-hidden', 'false');
            }
        });

        $menu.on('beforehide.smapi', (_e, menu) => {
            const $sub = $(menu);
            const $button = findButtonForSubmenu($sub);
            if ($button.length) {
                $button.attr('aria-expanded', 'false');
            }
        });

        // Use hide.smapi (fires after menuHide completes) rather than
        // beforehide.smapi so we don't reset aria-hidden when the hide is
        // cancelled (e.g. bindSmartMenusHideProtection returns false).
        $menu.on('hide.smapi', (_e, menu) => {
            const $sub = $(menu);
            if (isShowcaseWrapper($sub)) {
                $sub.find('.ss-submenu-links > ul').attr('aria-hidden', 'true');
            }
        });
    });
};

// =============================================================================
// MutationObserver — Strip aria-expanded from links
// =============================================================================

/**
 * SmartMenus continuously re-applies aria-expanded on <a> tags during its
 * menuShow / menuHide lifecycle (after our beforeshow/beforehide handlers).
 * A MutationObserver is the only reliable way to intercept these changes
 * because the SmartMenus events fire BEFORE the attribute is set.
 *
 * This observer watches the entire menu subtree for aria-expanded mutations
 * and removes the attribute from any <a> that has a sibling disclosure button,
 * ensuring only buttons carry the expanded state.
 */
const observeAndStripLinkAriaExpanded = (menuSelectors: readonly string[]): void => {
    menuSelectors.forEach((selector) => {
        const menuEl = document.querySelector<HTMLElement>(selector);
        if (!menuEl) {
            return;
        }

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.attributeName !== 'aria-expanded') {
                    continue;
                }
                const target = mutation.target as HTMLElement;
                if (target.tagName !== 'A') {
                    continue;
                }
                if ($(target).siblings(`.${DISCLOSURE_BUTTON_CLASS}`).length) {
                    target.removeAttribute('aria-expanded');
                }
            }
        });

        observer.observe(menuEl, {
            attributes: true,
            attributeFilter: ['aria-expanded'],
            subtree: true,
        });
    });
};

// =============================================================================
// Public Setup
// =============================================================================

/**
 * Set up SmartMenus synchronization: override link behavior,
 * bind keyboard/click handlers, sync button state with SmartMenus events,
 * and observe for stale aria-expanded on links.
 */
export const setupSmartMenusSync = (menuSelectors: readonly string[]): void => {
    $ = getJQuery();
    overrideSmartMenusLinkBehavior(menuSelectors);
    setupKeyboardHandlers(menuSelectors);
    setupSmartMenusEventListeners(menuSelectors);
    observeAndStripLinkAriaExpanded(menuSelectors);
};
